import React from 'react'
import { Upload, StickyNote, FileText, FolderOpen, Database, Hash, AlertCircle } from 'lucide-react'
import WorkPanelWrapper from './WorkPanelWrapper'

interface ActionStateViewProps {
  title: string
  categoryType: 'income' | 'assets' | 'collateral' | 'capacity' | 'documents' | 'flags' | 'default'
  description?: string
  onUploadDocument: () => void
  onCreateNote: () => void
  hasContent?: boolean // Whether the category already has content
}

const ActionStateView: React.FC<ActionStateViewProps> = ({
  title,
  categoryType,
  description,
  onUploadDocument,
  onCreateNote,
  hasContent = false
}) => {
  const getCategoryIcon = () => {
    switch (categoryType) {
      case 'income':
        return <FileText className="w-20 h-20 mx-auto text-gray-300" />
      case 'assets':
        return <Database className="w-20 h-20 mx-auto text-gray-300" />
      case 'collateral':
        return <FolderOpen className="w-20 h-20 mx-auto text-gray-300" />
      case 'capacity':
        return <Hash className="w-20 h-20 mx-auto text-gray-300" />
      case 'documents':
        return <FileText className="w-20 h-20 mx-auto text-gray-300" />
      case 'flags':
        return <AlertCircle className="w-20 h-20 mx-auto text-gray-300" />
      default:
        return <FileText className="w-20 h-20 mx-auto text-gray-300" />
    }
  }

  const getCategoryDescription = () => {
    if (description) return description
    
    const actionWord = hasContent ? 'Continue adding' : 'Start by adding'
    
    switch (categoryType) {
      case 'income':
        return `${actionWord} income verification documents like W2s, pay stubs, tax returns, or employment letters to strengthen this section.`
      case 'assets':
        return `${actionWord} bank statements, investment accounts, retirement accounts, or other asset documentation to complete the picture.`
      case 'collateral':
        return `${actionWord} property appraisals, purchase agreements, title reports, or other collateral-related documents.`
      case 'capacity':
        return `${actionWord} documents that demonstrate repayment capacity such as debt schedules, credit reports, or financial statements.`
      case 'documents':
        return `${actionWord} relevant documents for this case. They'll be automatically processed and extracted for analysis.`
      case 'flags':
        return `${actionWord} any concerns, discrepancies, or items requiring special attention for this case.`
      default:
        return `${actionWord} documents or notes to build this section. We'll help organize and analyze everything you add.`
    }
  }

  const getActionTitle = () => {
    if (hasContent) {
      switch (categoryType) {
        case 'income':
          return 'Add More Income Documentation'
        case 'assets':
          return 'Add More Asset Information'
        case 'collateral':
          return 'Update Collateral Details'
        case 'capacity':
          return 'Enhance Capacity Analysis'
        case 'documents':
          return 'Upload Additional Documents'
        case 'flags':
          return 'Note Additional Concerns'
        default:
          return 'Continue Building This Section'
      }
    }
    
    switch (categoryType) {
      case 'income':
        return 'Start Income Documentation'
      case 'assets':
        return 'Begin Asset Documentation'
      case 'collateral':
        return 'Add Collateral Information'
      case 'capacity':
        return 'Start Capacity Analysis'
      case 'documents':
        return 'Upload Your First Document'
      case 'flags':
        return 'No Flags or Issues Noted'
      default:
        return 'Get Started'
    }
  }

  return (
    <WorkPanelWrapper title={title}>
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-lg">
          <div className="mb-6">
            {getCategoryIcon()}
          </div>
          <h2 className="text-2xl font-semibold text-gray-700 mb-3">
            {getActionTitle()}
          </h2>
          <p className="text-gray-500 mb-8">
            {getCategoryDescription()}
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={onUploadDocument}
              className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center space-x-2 font-medium shadow-sm transition-colors"
            >
              <Upload className="w-5 h-5" />
              <span>{hasContent ? 'Add More Documents' : 'Upload Document'}</span>
            </button>
            <button
              onClick={onCreateNote}
              className="px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg flex items-center space-x-2 font-medium shadow-sm transition-colors"
            >
              <StickyNote className="w-5 h-5" />
              <span>{hasContent ? 'Add Note' : 'Create Note'}</span>
            </button>
          </div>
        </div>
      </div>
    </WorkPanelWrapper>
  )
}

export default ActionStateView
